import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import {
  CalendarDays,
  Pencil,
  Trash2,
  Facebook,
  Instagram,
  ChevronDown,
  Plus,
  Search,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import DataTables from '../../components/DataTables';
import NewFacebookPageConnection from '../../components/NewFacebookPageConnection';
import NewZohoConnection from '../../components/NewZohoConnection';
import NewGoogleConnection from '../../components/NewGoogleConnection';
import NewInstagramConnection from '../../components/NewInstagramConnection';
import NewPost from '../../components/NewPost';
import { setSelectedClient } from '../../store/slices/selectedClient';
import BulkModal from '../../components/BulkModal';
import NewClientModal from '../../components/NewClientModal';
import { getClients, updateClient } from '../../store/slices/clients';
import { authSelector } from '../../store/slices/auth';
import { deleteClient } from '../../store/slices/clients';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import { unwrapResult } from '@reduxjs/toolkit';
import { accountManagers } from '../../utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '../../components/ui/tooltip';
import { Badge } from '../../components/ui/badge';
import GoogleLogin from 'react-google-login';
import Swal from 'sweetalert2';
import {
  API_CLIENTS_NAME,
  API_KEY,
  API_FACEBOOK_PAGES,
  INSTAGRAM_SYNC,
  API_GOOGLE_LOCATIONS,
  API_SUPPORT
} from '../../utils/constants';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import yelpLogo from '../../Icons/yelp.svg';

// Helper function to format large numbers
const formatCount = (count) => {
  if (count === null || count === undefined) return '0';

  const num = Number(count);
  if (isNaN(num)) return '0';

  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

const isTikTokConnected = (socialNetworks) => {
  const tiktokConnection = socialNetworks?.tiktok;

  return Boolean(
    tiktokConnection === true ||
      (tiktokConnection &&
        typeof tiktokConnection === 'object' &&
        Object.keys(tiktokConnection).length > 0) ||
      socialNetworks?.tiktokAccountName ||
      socialNetworks?.tiktokUsername ||
      socialNetworks?.tiktokHandle ||
      socialNetworks?.tiktokOpenId
  );
};

const GENERIC_ERROR_MESSAGE = 'Social network error.';

const getErrorMessage = (errorMessage, fallbackMessage) =>
  typeof errorMessage === 'string' && errorMessage.trim()
    ? errorMessage
    : fallbackMessage || GENERIC_ERROR_MESSAGE;

const ErrorIndicator = ({ errorMessage, fallbackMessage, className }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        tabIndex={0}
        role="img"
        aria-label={getErrorMessage(errorMessage, fallbackMessage)}
        className={
          className ||
          'absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[9px] flex items-center justify-center font-bold outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1'
        }>
        !
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-md whitespace-normal break-words">
      {getErrorMessage(errorMessage, fallbackMessage)}
    </TooltipContent>
  </Tooltip>
);

const SocialIcon = ({
  type,
  hasError,
  errorMessage,
  fallbackMessage,
  small,
  active = true
}) => {
  const size = small ? 'h-6 w-6' : 'h-8 w-8';
  const iconSize = small ? 'h-3.5 w-3.5' : 'h-5 w-5';
  const svgSize = small ? 'h-5 w-5' : 'h-7 w-7';
  const inactiveClass = active ? '' : 'bg-gray-200';
  return (
    <div className="relative">
      {type === 'facebook' && (
        <div
          className={`${size} rounded-md ${
            active ? 'bg-[hsl(220,56%,47%)]' : inactiveClass
          } flex items-center justify-center`}>
          <svg
            className={`${iconSize} ${active ? 'text-white' : 'text-gray-400'}`}
            fill="currentColor"
            viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </div>
      )}
      {type === 'google' && (
        <div
          className={`${size} ${inactiveClass} flex items-center justify-center`}>
          <svg
            className={`${svgSize} ${active ? '' : 'grayscale'}`}
            viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        </div>
      )}
      {type === 'instagram' && (
        <div
          className={`${size} rounded-lg ${
            active
              ? 'bg-gradient-to-br from-[hsl(280,70%,50%)] via-[hsl(340,80%,55%)] to-[hsl(35,95%,55%)]'
              : inactiveClass
          } flex items-center justify-center`}>
          <svg
            className={`${iconSize} ${active ? 'text-white' : 'text-gray-400'}`}
            fill="currentColor"
            viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
        </div>
      )}
      {type === 'whatsapp' && (
        <div
          className={`${size} rounded-lg bg-[hsl(210,60%,50%)] flex items-center justify-center`}>
          <svg
            className={`${small ? 'h-3 w-3' : 'h-4 w-4'} text-white`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </div>
      )}
      {type === 'tiktok' && (
        <div
          className={`${size} rounded-lg ${
            active ? 'bg-black' : inactiveClass
          } flex items-center justify-center`}>
          <svg
            className={`${iconSize} ${active ? 'text-white' : 'text-gray-400'}`}
            viewBox="0 0 24 24"
            fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-5.2-1.74 2.89 2.89 0 0 1 2.31-1.26c.31 0 .61.05.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 1 0 15.86 15V8a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
          </svg>
        </div>
      )}
      {hasError && (
        <ErrorIndicator
          errorMessage={errorMessage}
          fallbackMessage={fallbackMessage}
        />
      )}
    </div>
  );
};

const YelpIcon = ({ small, active, onClick }) => {
  const size = small ? 'h-6 w-6' : 'h-8 w-8';
  const iconSize = small ? 'h-5 w-5' : 'h-7 w-7';

  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? 'Yelp active' : 'Set Yelp status'}
      className={`relative ${size} shrink-0 overflow-hidden rounded-md transition-opacity hover:opacity-80 ${
        active ? '' : 'grayscale opacity-60'
      }`}>
      <img src={yelpLogo} alt="Yelp" className={`${iconSize} object-cover`} />
    </button>
  );
};

const CountBadge = ({ value, variant = 'default', label }) => {
  const colors = {
    default: 'bg-muted text-muted-foreground border-border',
    warning:
      'bg-[hsl(30,90%,95%)] text-[hsl(30,80%,40%)] border-[hsl(30,60%,80%)]',
    success:
      'bg-[hsl(142,60%,95%)] text-[hsl(142,60%,30%)] border-[hsl(142,40%,75%)]'
  };
  return (
    <div className="flex items-center gap-1.5">
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      <span
        className={`inline-flex items-center justify-center h-6 sm:h-7 min-w-[24px] sm:min-w-[28px] px-1.5 rounded border text-xs font-semibold ${colors[variant]}`}>
        {value}
      </span>
    </div>
  );
};

const ClientCard = ({
  client,
  dispatch,
  history,
  onDelete,
  onSelectClient,
  onYelpClick,
  yelpActive
}) => {
  const handleCalendar = async () => {
    await onSelectClient(client);
    history.push('/calendar');
  };

  const handleEdit = async () => {
    await onSelectClient(client);
    history.push('/client');
  };

  const handleAppSettings = async () => {
    await onSelectClient(client);
    history.push('/app-settings');
  };

  // Obtener valores con fallbacks seguros
  const smsCount = client.smsCount || 0;
  const yelpClicks = client.yelpInfo?.clicks || 0;
  const gmbClicks = client.gmbInfo?.clicks || 0;
  const hasGoogleError = client.clientSocialNetworks?.gmbFailing || false;
  const hasFacebook = client.clientSocialNetworks?.facebook || false;
  const hasGoogle = client.clientSocialNetworks?.googleMyBusiness || false;
  const hasInstagram = client.clientSocialNetworks?.instagram || false;
  const hasTikTok = isTikTokConnected(client.clientSocialNetworks);
  // Renderizar notas
  const flagLabels = {
    NO_IG: 'No IG',
    NO_FB: 'No FB',
    NO_GMB: 'No GMB',
    NO_TIKTOK: 'No TikTok',
    NO_AI: 'No AI Images',
    NO_REVIEWS: 'No Reviews'
  };

  return (
    <div className="rounded-xl border bg-card p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <button
            onClick={handleEdit}
            className="text-primary hover:underline font-semibold text-sm text-left leading-tight">
            {client.clientName}......
          </button>
          <p className="text-xs text-muted-foreground mt-0.5">
            {client.accountOwnerFullName || 'No Manager'}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`text-[10px] shrink-0 ${
            client.accountStatus?.toLowerCase() === 'active'
              ? 'bg-[hsl(142,60%,95%)] text-[hsl(142,60%,30%)] border-[hsl(142,40%,75%)]'
              : 'bg-[hsl(0,60%,95%)] text-[hsl(0,60%,30%)] border-[hsl(0,40%,75%)]'
          }`}>
          {client.accountStatus || 'Unknown'}
        </Badge>
      </div>

      <div className="flex items-center gap-1">
        <SocialIcon
          type="facebook"
          hasError={client.clientSocialNetworks?.fbFailing && hasFacebook}
          errorMessage={client.clientSocialNetworks?.fbErrorMessage}
          fallbackMessage={GENERIC_ERROR_MESSAGE}
          small
        />
        <SocialIcon
          type="google"
          hasError={hasGoogleError && hasGoogle}
          errorMessage={client.clientSocialNetworks?.gmbErrorMessage}
          fallbackMessage={GENERIC_ERROR_MESSAGE}
          small
        />
        <SocialIcon
          type="instagram"
          hasError={client.clientSocialNetworks?.igFailing && hasInstagram}
          errorMessage={client.clientSocialNetworks?.igErrorMessage}
          fallbackMessage={GENERIC_ERROR_MESSAGE}
          small
        />
        <SocialIcon
          type="tiktok"
          active={hasTikTok}
          hasError={client.clientSocialNetworks?.tiktokFailing && hasTikTok}
          small
        />
        <SocialIcon type="whatsapp" small />
        <YelpIcon
          small
          active={yelpActive}
          onClick={() => onYelpClick(client)}
        />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <CountBadge
          value={client.appActive ? '✓' : '✗'}
          label="Review App"
          variant={client.appActive ? 'success' : 'default'}
        />
        <CountBadge
          value={smsCount}
          label="SMS"
          variant={smsCount > 0 ? 'warning' : 'default'}
        />
        <CountBadge
          value={yelpClicks}
          label="Yelp"
          variant={yelpClicks > 0 ? 'warning' : 'default'}
        />
        <CountBadge value={gmbClicks} label="GMB" />
      </div>

      {client.clientFlags && client.clientFlags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {client.clientFlags.map((flag, idx) => (
            <span
              key={idx}
              className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded inline-block">
              {flagLabels[flag] || flag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 border-t border-border pt-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-primary"
          onClick={handleCalendar}>
          <CalendarDays className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-primary"
          onClick={handleEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(client)}>
          <Trash2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 text-xs text-muted-foreground hover:text-primary"
          onClick={handleAppSettings}>
          App Settings
        </Button>
      </div>
    </div>
  );
};

const Dashboard = (props) => {
  const history = useHistory();
  const headerRef = useRef(null);
  const headerControlsRef = useRef(null);
  const [headerControlsWrapped, setHeaderControlsWrapped] = useState(false);
  const [managerFilter, setManagerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [networkFilterMode, setNetworkFilterMode] = useState('error');
  const [yelpAnswers, setYelpAnswers] = useState({});
  const [yelpPromptClientId, setYelpPromptClientId] = useState(null);
  const networkFilterOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'google', label: 'Google' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'yelp', label: 'Yelp' }
  ];
  const networkFilterModes = [
    { value: 'error', label: 'Errors' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  useEffect(() => {
    const persistedYelpAnswers = (props.customerList || []).reduce(
      (answers, client) => {
        answers[client.id] = Boolean(client.clientSocialNetworks?.yelp);
        return answers;
      },
      {}
    );
    setYelpAnswers(persistedYelpAnswers);
  }, [props.customerList]);

  const getNetworkStatus = (client, network) => {
    const socialNetworks = client.clientSocialNetworks || {};
    const connected = {
      facebook: Boolean(socialNetworks.facebook),
      google: Boolean(socialNetworks.googleMyBusiness),
      instagram: Boolean(socialNetworks.instagram),
      tiktok: isTikTokConnected(socialNetworks),
      yelp: Boolean(yelpAnswers[client.id])
    }[network];
    const failing = {
      facebook: Boolean(socialNetworks.fbFailing),
      google: Boolean(socialNetworks.gmbFailing),
      instagram: Boolean(socialNetworks.igFailing),
      tiktok: Boolean(socialNetworks.tiktokFailing),
      yelp: false
    }[network];

    if (networkFilterMode === 'error') return connected && failing;
    if (networkFilterMode === 'active') return connected && !failing;
    if (networkFilterMode === 'inactive') return !connected;
    return true;
  };

  const openYelpPrompt = (client) => {
    setYelpPromptClientId(client.id);
  };

  const saveYelpStatus = async (clientId) => {
    const client = (props.customerList || []).find(
      (item) => item.id === clientId
    );
    if (!client) {
      throw new Error('Client not found');
    }

    const yelpActive = !Boolean(client.clientSocialNetworks?.yelp);
    const socialNetworks = {
      ...(client.clientSocialNetworks || {})
    };
    if (yelpActive) {
      socialNetworks.yelp = true;
    } else {
      delete socialNetworks.yelp;
    }

    await dispatch(
      updateClient({
        id: client.id,
        socialNetworks
      })
    ).unwrap();

    setYelpAnswers((answers) => ({
      ...answers,
      [clientId]: yelpActive
    }));
    await dispatch(getClients()).unwrap();
  };
  // Obtener lista de managers únicos
  const managers = Array.from(
    new Set(
      (props.customerList || [])
        .map((c) => c.accountOwnerFullName)
        .filter(Boolean)
    )
  );
  // Filtrar clientes según manager y búsqueda (por clientName, status y notes)
  const filteredClients = (props.customerList || []).filter((c) => {
    const matchesManager =
      managerFilter === 'all' || c.accountOwnerFullName === managerFilter;
    const lowerSearch = searchTerm.trim().toLowerCase();
    const notesLabels = {
      NO_IG: 'No IG',
      NO_FB: 'No FB',
      NO_GMB: 'No GMB',
      NO_TIKTOK: 'No TikTok',
      NO_AI: 'No AI Images',
      NO_REVIEWS: 'No Reviews'
    };
    const notesText = Array.isArray(c.clientFlags)
      ? c.clientFlags.map((flag) => notesLabels[flag] || flag).join(' ')
      : c.searchableNotes || '';
    const matchesSearch =
      !searchTerm ||
      (c.clientName && c.clientName.toLowerCase().includes(lowerSearch)) ||
      (c.accountStatus &&
        c.accountStatus.toLowerCase().includes(lowerSearch)) ||
      (notesText && notesText.toLowerCase().includes(lowerSearch));
    const matchesNetwork =
      selectedNetworks.length === 0 ||
      selectedNetworks.every((network) => getNetworkStatus(c, network));
    return matchesManager && matchesSearch && matchesNetwork;
  });

  const toggleNetworkFilter = (network, checked) => {
    setSelectedNetworks((currentNetworks) =>
      checked
        ? [...currentNetworks, network]
        : currentNetworks.filter((currentNetwork) => currentNetwork !== network)
    );
  };
  useEffect(() => {
    const header = headerRef.current;
    const controls = headerControlsRef.current;
    if (!header || !controls) return undefined;

    let frame = 0;

    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const title = header.firstElementChild;
        if (!title) return;
        const wrapped =
          controls.offsetTop >= title.offsetTop + title.offsetHeight - 4;
        setHeaderControlsWrapped((current) =>
          current === wrapped ? current : wrapped
        );
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  // Modal state - controlled pattern
  const [facebookModalOpen, setFacebookModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [instagramModalOpen, setInstagramModalOpen] = useState(false);
  const [zohoModalOpen, setZohoModalOpen] = useState(false);
  const [newClientModalOpen, setNewClientModalOpen] = useState(false);
  // Delete client modal states
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState(false);
  const [syncNetworkErrorModalOpen, setSyncNetworkErrorModalOpen] =
    useState(false);
  const [facebookModalClient, setFacebookModalClient] = useState(null);
  const [googleModalClient, setGoogleModalClient] = useState(null);
  const [instagramModalClient, setInstagramModalClient] = useState(null);
  const [zohoModalClient, setZohoModalClient] = useState(null);

  // Sync modal state (global sync, not per-client)
  const [fbSyncOpen, setFbSyncOpen] = useState(false);
  const [igSyncOpen, setIgSyncOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [fbSyncLoading, setFbSyncLoading] = useState(false);
  const [igSyncLoading, setIgSyncLoading] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [fbSyncPageId, setFbSyncPageId] = useState('');
  const [igSyncPageId, setIgSyncPageId] = useState('');
  const [danielToken, setDanielToken] = useState('');
  const [robinToken, setRobinToken] = useState('');
  const [syncSuccess, setSyncSuccess] = useState({ open: false, title: '' });

  const [selectedClientToDelete, setSelectedClientToDelete] = useState(null);

  const [currentClientId, setCurrentClientId] = useState(null);
  const [currentClientTimeZone, setCurrentClientTimeZone] = useState(null);
  const [currentClientBestTimes, setCurrentClientBestTimes] = useState(null);
  const [currentClientMergeFields, setCurrentClientMergeFields] =
    useState(null);
  const [currentClientSocialNetworks, setCurrentClientSocialNetworks] =
    useState([]);
  const [currentClient, setCurrentClient] = useState(null);
  const instagramPages = [];
  const [loading, setLoading] = useState(false);
  const [newPost, setNewPost] = useState({ show: false });
  const [showModal, setShowModal] = useState(false);
  const handleShow = () => setShowModal(true);
  const dispatch = useDispatch();
  const { userInfo } = useSelector(authSelector);
  const { user: clerkUser } = useUser();

  // Fetch complete client data before selecting (to include logoUrl and all attributes)
  const handleSelectClient = async (client) => {
    if (!client) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${client.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including logoUrl
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(client));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(client));
    }
  };

  const accountStatusColumn = (cell, row) => {
    if (row.id) {
      return (
        <div>
          <span>{row.accountStatus}</span>
        </div>
      );
    }
    return <div></div>;
  };

  const accountManagersColumn = (cell, row) => {
    const accountManager = accountManagers.find(
      (manager) => manager.id === row.accountOwner
    );

    if (accountManager) {
      return (
        <div>
          <span>{accountManager.full_name}</span>
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  const validNetworks = (socialnetworks) => {
    return (
      socialnetworks.googleMyBusiness ||
      socialnetworks.facebook ||
      socialnetworks.instagram
    );
  };
  const callBackClose = () => {
    setNewPost({ show: false });
  };

  const handleCalendarClick = async (row) => {
    await handleSelectClient(row);
    let newUrl = row.clientName
      .replace(/\s+/g, '%20')
      .replace(/\//g, '%2F')
      .replace(/'/g, '%1D')
      .replace(/’/g, '%1E');
    props.history.push('/calendar');
  };

  const handleGoogleSync = (response) => {
    setGoogleSyncing(true);
    axios({
      method: 'POST',
      url: API_GOOGLE_LOCATIONS,
      data: { code: response.code },
      headers: { Authorization: `bearer ${userInfo.accessToken}` }
    })
      .then(() => {
        setGoogleSyncing(false);
        setSyncSuccess({
          open: true,
          title: 'Google My Business sync complete!'
        });
      })
      .catch((error) => {
        setGoogleSyncing(false);
        console.error('Google sync error', error);
      });
  };

  // Sync flows for the dropdown (global, not per-client)
  const SocialNetworksDropdown = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="flex h-12 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground shadow-lg transition-all hover:bg-primary/90 hover:shadow-xl sm:h-14 sm:px-4 sm:text-base">
          Connect Social Networks
          <ChevronDown className="h-4 w-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72 bg-card z-50 p-2 rounded-xl shadow-xl">
          <DropdownMenuItem
            className="gap-3 cursor-pointer py-3 px-4 rounded-lg text-base"
            onClick={() => {
              setFbSyncPageId('');
              setFbSyncOpen(true);
            }}>
            <Facebook className="h-5 w-5 text-[hsl(220,56%,47%)]" />
            Connect Facebook Account
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-3 cursor-pointer py-3 px-4 rounded-lg text-base"
            onClick={() => {
              setIgSyncPageId('');
              setIgSyncOpen(true);
            }}>
            <Instagram className="h-5 w-5 text-[hsl(330,70%,50%)]" />
            Connect Instagram Account
          </DropdownMenuItem>
          <GoogleLogin
            clientId="968920648651-23di9ci83j1md3k0bah3t4g18br1vo24.apps.googleusercontent.com"
            render={(renderProps) => (
              <DropdownMenuItem
                className="gap-3 cursor-pointer py-3 px-4 rounded-lg text-base"
                onClick={renderProps.onClick}
                disabled={renderProps.disabled || googleSyncing}>
                {googleSyncing ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )}
                {googleSyncing
                  ? 'Syncing...'
                  : 'Connect Google Business Account'}
              </DropdownMenuItem>
            )}
            onSuccess={handleGoogleSync}
            onFailure={() => {
              Swal.fire({
                icon: 'info',
                title: 'Google Sync Cancelled',
                showConfirmButton: false,
                timer: 3500
              });
            }}
            cookiePolicy="single_host_origin"
            scope="email https://www.googleapis.com/auth/plus.business.manage"
            accessType="offline"
            responseType="code"
            prompt="consent"
          />
          <DropdownMenuItem
            className="gap-3 cursor-pointer py-3 px-4 rounded-lg text-base"
            onClick={() => {
              setDanielToken('');
              setRobinToken('');
              setSupportOpen(true);
            }}>
            <span className="h-5 w-5 inline-block bg-primary rounded-full" />
            Support
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Definir clientActions para evitar error de no definido
  const clientActions = () => null;

  const handleDelete = (selectedClient) => {
    setSelectedClientToDelete(selectedClient);
    setDeleteConfirmModalOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!selectedClientToDelete || !selectedClientToDelete.id) {
      setDeleteConfirmModalOpen(false);
      setSelectedClientToDelete(null);
      setDeleteWarningModalOpen(true);
      return;
    }

    let newGroups = [];
    if (Array.isArray(selectedClientToDelete.clientGroups)) {
      selectedClientToDelete.clientGroups.forEach((clientGroup) => {
        if (clientGroup && clientGroup.id) {
          newGroups.push({ id: clientGroup.id });
        }
      });
    } else if (selectedClientToDelete.clientGroups) {
      newGroups.push({ id: selectedClientToDelete.clientGroups });
    }

    try {
      const clerkUserEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';
      const clerkUserDisplayName = clerkUser?.firstName
        ? `${clerkUser.firstName}${
            clerkUser.lastName ? ' ' + clerkUser.lastName : ''
          }`
        : clerkUserEmail;

      // Determinar rol basado en dominio de email
      const userRole =
        clerkUserEmail.endsWith('@adlerlocal.com') ||
        clerkUserEmail.endsWith('@samxlabs.com') ||
        clerkUserEmail.endsWith('@adlersocial.com')
          ? 'admin'
          : 'user';

      const payload = {
        groupsDelete: newGroups,
        clientId: selectedClientToDelete.id,
        deletedBy: {
          displayName: clerkUserDisplayName,
          email: clerkUserEmail,
          role: userRole
        }
      };
      let res = await dispatch(deleteClient(payload));
      res = unwrapResult(res);

      setDeleteConfirmModalOpen(false);
      setSelectedClientToDelete(null);

      if (res) {
        await undercoverGetClients();
        setDeleteSuccessModalOpen(true);
      } else {
        setDeleteWarningModalOpen(true);
      }
    } catch (e) {
      console.error(e);
      setDeleteConfirmModalOpen(false);
      setSelectedClientToDelete(null);
      setDeleteWarningModalOpen(true);
    }
  };

  const renderZohoIcon = (active) => {
    let iconStyle = {
      width: 23,
      height: 23,
      backgroundImage: `url(${
        active
          ? `${process.env.PUBLIC_URL}/assets/img/logo-crm.png`
          : `${process.env.PUBLIC_URL}/assets/img/logo-crm-disconnected.png`
      })`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat'
    };
    return <div style={iconStyle} />;
  };

  const clientSocialNetworks = (cell, row) => {
    if (row.id) {
      return (
        <>
          <nav className="flex justify-center">
            {row.clientSocialNetworks ? (
              <></>
            ) : (
              <p style={{ color: 'red' }}>
                Error trying to load social networks, please contact support
              </p>
            )}
          </nav>
        </>
      );
    }
  };

  const clientName = (cell, row) => {
    if (row.id) {
      let newUrl = row.clientName
        .replace(/\s+/g, '%20')
        .replace(/\//g, '%2F')
        .replace(/'/g, '%1D')
        .replace(/’/g, '%1E');
      return (
        <div>
          <button
            onClick={async () => {
              await handleSelectClient(row);
              props.history.push({
                pathname: `/client/${newUrl}`,
                state: {
                  clientId: row.id,
                  clientGroups: row.clientGroups,
                  socialNetworks: row['clientSocialNetworks']
                }
              });
            }}
            className="bg-transparent border-none cursor-pointer p-0">
            <span>{row['clientName']}..............</span>
          </button>
        </div>
      );
    }
  };

  const renderAppIcon = (active) => {
    let iconStyle = {
      width: 30,
      height: 30,
      backgroundImage: `url(${
        active
          ? `${process.env.PUBLIC_URL}/assets/img/sms.png`
          : `${process.env.PUBLIC_URL}/assets/img/smsf.png`
      })`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat'
    };
    return <div style={iconStyle} />;
  };

  const handleAppRegisterClick = async (row) => {
    await handleSelectClient(row);
    props.history.push('/app-settings');
  };

  const mobileApp = (cell, row) => {
    if (row.id) {
      return (
        <>
          <nav className="flex justify-center items-center">
            <button
              className="btn-icon button-like-a"
              style={{ marginRight: 10 }}
              onClick={() => handleAppRegisterClick(row)}>
              {renderAppIcon(row.appActive)}
            </button>
            <div className="flex gap-1 items-center justify-center">
              <div
                className="flex items-center justify-center min-w-[32px] w-8 h-7 rounded border bg-green-50 border-green-200"
                title={`SMS Count: ${
                  typeof row.smsCount === 'undefined' ? '0' : row.smsCount
                }`}>
                <p className="text-xs text-green-600 m-0 leading-none">
                  {formatCount(
                    typeof row.smsCount === 'undefined' ? '0' : row.smsCount
                  )}
                </p>
              </div>
              <div
                className="flex items-center justify-center min-w-[32px] w-8 h-7 rounded border bg-red-50 border-red-200"
                title={`Yelp Clicks: ${
                  row.yelpInfo ? row.yelpInfo.clicks : 0
                }`}>
                <p className="text-xs text-red-600 m-0 leading-none">
                  {formatCount(row.yelpInfo ? row.yelpInfo.clicks : 0)}
                </p>
              </div>
              <div
                className="flex items-center justify-center min-w-[32px] w-8 h-7 rounded border bg-blue-50 border-blue-200"
                title={`Google My Business Clicks: ${
                  row.gmbInfo ? row.gmbInfo.clicks : 0
                }`}>
                <p className="text-xs text-blue-600 m-0 leading-none">
                  {formatCount(row.gmbInfo ? row.gmbInfo.clicks : 0)}
                </p>
              </div>
            </div>
          </nav>
        </>
      );
    }
  };

  const clientNotes = (cell, row) => {
    if (row.id && row.clientFlags && row.clientFlags.length > 0) {
      const flagLabels = {
        NO_IG: 'No IG',
        NO_FB: 'No FB',
        NO_GMB: 'No GMB',
        NO_TIKTOK: 'No TikTok',
        NO_AI: 'No AI Images',
        NO_REVIEWS: 'No Reviews'
      };

      return (
        <div className="flex flex-wrap justify-center gap-1">
          {row.clientFlags.map((flag, index) => (
            <span
              key={index}
              className="text-[10px] py-0 px-1.5 rounded bg-gray-100 text-gray-500 border border-gray-200 leading-tight"
              title={flagLabels[flag] || flag}>
              {flagLabels[flag] || flag}
            </span>
          ))}
        </div>
      );
    }
    return <div></div>;
  };
  // Update table only if differences un clients list

  // Fetch all clients to listen for changes
  const undercoverGetClients = async () => {
    dispatch(showLoading());
    setLoading(true);
    try {
      await dispatch(getClients()).unwrap();
      dispatch(hideLoading());

      setLoading(false);
    } catch (error) {
      dispatch(hideLoading());

      setLoading(false);
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    undercoverGetClients();
    // eslint-disable-next-line
  }, []);

  const myCallback = (dataFromChild) => {
    undercoverGetClients();
  };

  const addSearchableNotesField = (data) => {
    return data.map((item) => {
      if (item.clientFlags && Array.isArray(item.clientFlags)) {
        const flagLabels = {
          NO_IG: 'No IG',
          NO_FB: 'No FB',
          NO_GMB: 'No GMB',
          NO_TIKTOK: 'No TikTok',
          NO_AI: 'No AI Images',
          NO_REVIEWS: 'No Reviews'
        };

        const readableNotes = item.clientFlags
          .map((flag) => flagLabels[flag] || flag)
          .join(' ');
        return {
          ...item,
          searchableNotes: readableNotes
        };
      }
      return {
        ...item,
        searchableNotes: ''
      };
    });
  };

  const clientColumns = [
    {
      dataField: 'id',
      text: 'Id',
      hidden: true
    },
    {
      dataField: 'searchableNotes',
      text: 'Searchable Notes',
      hidden: true
    },
    {
      dataField: 'clientName',
      text: 'Client Name',
      headerStyle: () => {
        return {
          width: '281px',
          textAlign: 'left',
          marginLeft: '15px'
        };
      },
      style: () => {
        return {
          paddingTop: '12px',
          textAlign: 'left',
          marginLeft: '10px'
        };
      },
      formatter: clientName
    },
    {
      dataField: 'accountOwnerFullName',
      text: 'Account Manager',
      headerStyle: () => {
        return {
          width: '180px',
          textAlign: 'left',
          marginLeft: '15px'
        };
      },
      style: () => {
        return {
          paddingTop: '12px',
          textAlign: 'left',
          marginLeft: '10px'
        };
      },
      formatter: accountManagersColumn
    },
    {
      dataField: 'accountStatus',
      text: 'Status',
      headerStyle: () => {
        return {
          width: '110px',
          textAlign: 'center',
          marginLeft: '15px'
        };
      },
      style: () => {
        return {
          paddingTop: '12px',
          textAlign: 'center',
          marginLeft: '10px'
        };
      },
      formatter: accountStatusColumn
    },
    {
      dataField: 'dummy-sn',
      text: 'Social Networks',
      formatter: clientSocialNetworks,
      headerStyle: () => {
        return {
          width: '180px',
          textAlign: 'center'
        };
      }
    },
    {
      dataField: 'appActive',
      text: 'App - #SMS  Yelp  GMB',
      headerStyle: () => {
        return { width: '200px', textAlign: 'center' };
      },
      formatter: mobileApp
    },
    {
      dataField: 'clientFlags',
      text: 'Notes',
      headerStyle: () => {
        return { width: '140px', textAlign: 'center' };
      },
      formatter: clientNotes
    },
    {
      dataField: 'dummy-actions',
      text: 'Actions',
      formatter: clientActions,
      headerStyle: () => {
        return {
          width: '160px',
          textAlign: 'center',
          alignItems: 'center'
        };
      }
    }
  ];

  const connectSnCallback = () => {
    myCallback();
  };

  const dataClientHandler = (
    clientId,
    socialnetworks,
    timeZone,
    bestTimes,
    mergeFields
  ) => {
    setCurrentClientId(clientId);
    setCurrentClientSocialNetworks(socialnetworks);
    setCurrentClientTimeZone(timeZone);
    setCurrentClientBestTimes(bestTimes);
    setCurrentClientMergeFields(mergeFields);
    let validSn = validNetworks(socialnetworks);
    if (!validSn) {
      setSyncNetworkErrorModalOpen(true);
    }
  };
  const userRole = props.role;

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Sidebar placeholder if needed */}
      {/* <Sidebar /> */}
      <main className="flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ml-0">
        <div className="p-3 sm:p-6 lg:p-10 space-y-4 sm:space-y-8">
          {/* Header */}
          <div
            ref={headerRef}
            className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-3 sm:mb-4">
            <div className="min-w-max max-w-full shrink-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
                All Clients ({filteredClients.length})
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-0.5 sm:mt-1">
                View, add, and manage all your client accounts
              </p>
            </div>
            <div
              ref={headerControlsRef}
              className={`flex max-w-full shrink-0 flex-wrap items-center gap-2 ${
                headerControlsWrapped ? 'justify-start' : 'ml-auto justify-end'
              }`}>
              <div className="relative w-72 max-w-full shrink-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Search"
                  className="h-14 w-full rounded-xl bg-white pl-12 text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={managerFilter} onValueChange={setManagerFilter}>
                <SelectTrigger className="h-12 w-28 shrink-0 rounded-xl bg-white text-sm sm:h-14 sm:w-32 sm:text-base">
                  <SelectValue placeholder="Account Manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex shrink-0 items-center gap-2">
                <SocialNetworksDropdown />
                <Button
                  className="h-12 shrink-0 gap-2 rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground shadow-lg transition-all hover:bg-primary/90 hover:shadow-xl sm:h-14 sm:px-4 sm:text-base"
                  onClick={() => history.push('/new-client-onboarding')}>
                  <Plus className="h-5 w-5" />
                  Add New Client
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <span className="text-sm font-medium text-muted-foreground">
              Filter by social network:
            </span>
            {networkFilterOptions.map((network) => (
              <label
                key={network.value}
                className="flex cursor-pointer select-none items-center gap-2 text-sm font-medium text-foreground">
                <Checkbox
                  className="h-5 w-5"
                  checked={selectedNetworks.includes(network.value)}
                  onCheckedChange={(checked) =>
                    toggleNetworkFilter(network.value, checked === true)
                  }
                />
                <span>{network.label}</span>
              </label>
            ))}
            <Select
              value={networkFilterMode}
              onValueChange={setNetworkFilterMode}>
              <SelectTrigger className="h-10 w-36 rounded-xl bg-white text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {networkFilterModes.map((mode) => (
                  <SelectItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:hidden space-y-3">
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                dispatch={props.dispatch}
                history={history}
                onDelete={handleDelete}
                onSelectClient={handleSelectClient}
                onYelpClick={openYelpPrompt}
                yelpActive={Boolean(yelpAnswers[client.id])}
              />
            ))}
          </div>
          {/* Table styles adapted from lovable */}
          <div className="hidden sm:block rounded-xl border bg-card shadow-sm overflow-x-auto">
            <table className="min-w-full divide-y divide-muted">
              <thead className="bg-muted/50">
                <tr>
                  <th className="font-semibold text-foreground px-4 py-3 text-left">
                    Client Name
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-left w-[220px]">
                    Account Manager
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-left w-[110px]">
                    Status
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[180px]">
                    Social Networks
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[60px]">
                    Review App
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[60px]">
                    SMS Sent
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[60px]">
                    Yelp Reviews
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[60px]">
                    Google Reviews
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-left w-[180px]">
                    Notes
                  </th>
                  <th className="font-semibold text-foreground px-4 py-3 text-center w-[120px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length > 0 ? (
                  filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-muted/30">
                      {/* Cell renderers will be adapted next step */}
                      <td className="px-4 py-2 text-primary font-medium text-sm">
                        <button
                          onClick={async () => {
                            await handleSelectClient(client);
                            history.push('/client');
                          }}
                          className="text-primary hover:underline font-medium text-sm bg-transparent border-none cursor-pointer p-0">
                          {client.clientName}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-sm text-foreground">
                        {client.accountOwnerFullName}
                      </td>
                      <td className="px-4 py-2">
                        {client.accountStatus && (
                          <span
                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                              client.accountStatus.toLowerCase() === 'active'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-red-50 text-red-700 border-red-200'
                            }`}>
                            {client.accountStatus}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Facebook SVG + fail indicator */}
                          <button
                            onClick={() => {
                              setFacebookModalClient(client);
                              setFacebookModalOpen(true);
                            }}
                            className={`relative h-8 w-8 rounded-md flex items-center justify-center transition-all hover:scale-110 cursor-pointer ${
                              client.clientSocialNetworks?.facebook
                                ? 'bg-[hsl(220,56%,47%)] hover:bg-[hsl(220,56%,40%)]'
                                : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                            title={
                              client.clientSocialNetworks?.facebook
                                ? `Connected: ${
                                    client.clientSocialNetworks.facebookPage ||
                                    'Facebook'
                                  }`
                                : 'Click to connect Facebook'
                            }>
                            <svg
                              className={`h-5 w-5 ${
                                client.clientSocialNetworks?.facebook
                                  ? 'text-white'
                                  : 'text-gray-400'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 24 24">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                            {client.clientSocialNetworks?.fbFailing &&
                              client.clientSocialNetworks?.facebook && (
                                <ErrorIndicator
                                  errorMessage={
                                    client.clientSocialNetworks?.fbErrorMessage
                                  }
                                  fallbackMessage={GENERIC_ERROR_MESSAGE}
                                  className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow animate-pulse outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                />
                              )}
                          </button>
                          {/* Google Button + fail indicator */}
                          <button
                            onClick={() => {
                              setGoogleModalClient(client);
                              setGoogleModalOpen(true);
                            }}
                            className={`relative h-8 w-8 flex items-center justify-center rounded transition-transform hover:scale-110 cursor-pointer ${
                              client.clientSocialNetworks?.googleMyBusiness
                                ? ''
                                : 'bg-gray-200'
                            }`}>
                            <svg
                              className={`h-7 w-7 ${
                                client.clientSocialNetworks?.googleMyBusiness
                                  ? ''
                                  : 'grayscale'
                              }`}
                              viewBox="0 0 24 24">
                              <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                              />
                            </svg>
                            {client.clientSocialNetworks?.gmbFailing &&
                              client.clientSocialNetworks?.googleMyBusiness && (
                                <ErrorIndicator
                                  errorMessage={
                                    client.clientSocialNetworks?.gmbErrorMessage
                                  }
                                  fallbackMessage={GENERIC_ERROR_MESSAGE}
                                  className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow animate-pulse outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                />
                              )}
                          </button>
                          {/* Instagram Button + fail indicator */}
                          <button
                            onClick={() => {
                              setInstagramModalClient(client);
                              setInstagramModalOpen(true);
                            }}
                            className={`relative h-8 w-8 rounded-lg flex items-center justify-center transition-transform hover:scale-110 cursor-pointer ${
                              client.clientSocialNetworks?.instagram
                                ? 'bg-gradient-to-br from-[hsl(280,70%,50%)] via-[hsl(340,80%,55%)] to-[hsl(35,95%,55%)]'
                                : 'bg-gray-200'
                            }`}>
                            <svg
                              className={`h-5 w-5 ${
                                client.clientSocialNetworks?.instagram
                                  ? 'text-white'
                                  : 'text-gray-400'
                              }`}
                              fill="currentColor"
                              viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                            </svg>
                            {client.clientSocialNetworks?.igFailing &&
                              client.clientSocialNetworks?.instagram && (
                                <ErrorIndicator
                                  errorMessage={
                                    client.clientSocialNetworks?.igErrorMessage
                                  }
                                  fallbackMessage={GENERIC_ERROR_MESSAGE}
                                  className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow animate-pulse outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                />
                              )}
                          </button>
                          {/* TikTok indicator (connection is managed elsewhere) */}
                          <button
                            className={`relative h-8 w-8 rounded-lg flex items-center justify-center cursor-default ${
                              isTikTokConnected(client.clientSocialNetworks)
                                ? 'bg-black'
                                : 'bg-gray-200'
                            }`}
                            title={
                              isTikTokConnected(client.clientSocialNetworks)
                                ? 'TikTok connected'
                                : 'TikTok not connected'
                            }
                            type="button">
                            <svg
                              className={`h-5 w-5 ${
                                isTikTokConnected(client.clientSocialNetworks)
                                  ? 'text-white'
                                  : 'text-gray-400'
                              }`}
                              viewBox="0 0 24 24"
                              fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-5.2-1.74 2.89 2.89 0 0 1 2.31-1.26c.31 0 .61.05.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 1 0 15.86 15V8a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
                            </svg>
                            {client.clientSocialNetworks?.tiktokFailing &&
                              isTikTokConnected(
                                client.clientSocialNetworks
                              ) && (
                                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow animate-pulse">
                                  !
                                </span>
                              )}
                          </button>
                          <YelpIcon
                            active={Boolean(yelpAnswers[client.id])}
                            onClick={() => openYelpPrompt(client)}
                          />
                          {/* Zoho Button - no fail indicator known */}
                          <button
                            onClick={() => {
                              setZohoModalClient(client);
                              setZohoModalOpen(true);
                            }}
                            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-transform hover:scale-110 cursor-pointer ${
                              client.clientSocialNetworks?.zoho
                                ? 'bg-[hsl(210,60%,50%)]'
                                : 'bg-gray-200'
                            }`}>
                            <svg
                              className={`h-4 w-4 ${
                                client.clientSocialNetworks?.zoho
                                  ? 'text-white'
                                  : 'text-gray-400'
                              }`}
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24">
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          className={`h-8 w-8 rounded-lg flex items-center justify-center mx-auto hover:opacity-80 transition-opacity ${
                            client.appActive
                              ? 'bg-[hsl(82,70%,52%)]'
                              : 'bg-gray-200'
                          }`}
                          onClick={async () => {
                            if (!client || !client.id) {
                              setDeleteWarningModalOpen(true);
                              return;
                            }
                            await handleSelectClient(client);
                            if (props.history) {
                              props.history.push({
                                pathname: '/app-settings',
                                state: {
                                  clientId: client.id,
                                  clientGroups: client.clientGroups || [],
                                  socialNetworks:
                                    client['clientSocialNetworks'] || {}
                                }
                              });
                            }
                          }}
                          title="App Settings">
                          <svg
                            className={`h-4 w-4 ${
                              client.appActive ? 'text-white' : 'text-gray-400'
                            }`}
                            fill="currentColor"
                            viewBox="0 0 24 24">
                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
                            <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
                          </svg>
                        </button>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center justify-center h-7 min-w-[28px] px-1.5 rounded border text-xs font-semibold bg-muted text-muted-foreground border-border">
                          {client.smsCount || 0}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center justify-center h-7 min-w-[28px] px-1.5 rounded border text-xs font-semibold bg-muted text-muted-foreground border-border">
                          {client.yelpInfo ? client.yelpInfo.clicks : 0}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="inline-flex items-center justify-center h-7 min-w-[28px] px-1.5 rounded border text-xs font-semibold bg-muted text-muted-foreground border-border">
                          {client.gmbInfo ? client.gmbInfo.clicks : 0}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {Array.isArray(client.clientFlags) &&
                        client.clientFlags.length > 0 ? (
                          <div className="w-full flex flex-col items-center">
                            <div className="flex flex-wrap justify-center gap-x-2 gap-y-1 max-w-[220px]">
                              {client.clientFlags.map((flag, idx) => {
                                const flagLabels = {
                                  NO_IG: 'No IG',
                                  NO_FB: 'No FB',
                                  NO_GMB: 'No GMB',
                                  NO_TIKTOK: 'No TikTok',
                                  NO_AI: 'No AI Images',
                                  NO_REVIEWS: 'No Reviews'
                                };
                                return (
                                  <span
                                    key={idx}
                                    className="text-[10px] py-0 px-1 text-center rounded bg-gray-100 text-gray-500 border border-gray-200 leading-tight inline-flex items-center justify-center whitespace-nowrap"
                                    title={flagLabels[flag] || flag}>
                                    {flagLabels[flag] || flag}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          client.searchableNotes && (
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                              {client.searchableNotes}
                            </span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1 min-w-[140px]">
                          {/* Calendar */}
                          <button
                            className="flex items-center justify-center w-9 h-9 bg-transparent border-none cursor-pointer text-muted-foreground hover:text-primary"
                            type="button"
                            onClick={async () => {
                              // Simula handleCalendarClick
                              await handleSelectClient(client);
                              let newUrl = client.clientName
                                .replace(/\s+/g, '%20')
                                .replace(/\//g, '%2F')
                                .replace(/'/g, '%1D')
                                .replace(/’/g, '%1E');
                              if (props.history) {
                                props.history.push('/calendar');
                              }
                            }}
                            title="Calendar">
                            <CalendarDays className="h-4 w-4" />
                          </button>
                          {/* Edit */}
                          <button
                            onClick={async () => {
                              await handleSelectClient(client);
                              history.push('/client');
                            }}
                            className="flex items-center justify-center w-9 h-9 bg-transparent border-none cursor-pointer text-muted-foreground hover:text-primary"
                            title="Edit Client">
                            <Pencil className="h-4 w-4" />
                          </button>
                          {/* Delete */}
                          <button
                            className="flex items-center justify-center w-9 h-9 bg-transparent border-none cursor-pointer text-muted-foreground hover:text-destructive"
                            type="button"
                            onClick={() => {
                              setSelectedClientToDelete(client);
                              setDeleteConfirmModalOpen(true);
                            }}
                            title="Delete client">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-6 text-muted-foreground">
                      No Clients
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmModalOpen}
            onOpenChange={(open) => {
              if (!open) {
                setDeleteConfirmModalOpen(false);
                setSelectedClientToDelete(null);
              }
            }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader className="text-center sm:text-center">
                <DialogTitle className="text-xl font-bold text-foreground">
                  Delete Client
                </DialogTitle>
                <DialogDescription className="text-base text-muted-foreground">
                  Are you sure you want to delete?
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-row justify-center gap-3 sm:justify-center py-4">
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 font-semibold rounded-xl h-12"
                  onClick={confirmDeleteClient}>
                  OK
                </button>
                <button
                  className="bg-secondary px-8 font-semibold rounded-xl h-12 border border-muted"
                  onClick={() => {
                    setDeleteConfirmModalOpen(false);
                    setSelectedClientToDelete(null);
                  }}>
                  CANCEL
                </button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={yelpPromptClientId !== null}
            onOpenChange={(open) => {
              if (!open) setYelpPromptClientId(null);
            }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader className="text-center sm:text-center">
                <DialogTitle className="text-xl font-bold text-foreground">
                  Yelp
                </DialogTitle>
                <DialogDescription className="text-base text-muted-foreground">
                  {yelpPromptClientId !== null &&
                  yelpAnswers[yelpPromptClientId]
                    ? 'Do you want to deactivate Yelp for this client?'
                    : 'Is Yelp active for this client?'}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-row justify-center gap-3 py-4">
                <button
                  className="h-12 rounded-xl bg-primary px-8 font-semibold text-primary-foreground hover:bg-primary/90"
                  onClick={() => {
                    if (yelpPromptClientId !== null) {
                      saveYelpStatus(yelpPromptClientId).catch((error) => {
                        console.error('Error updating Yelp status:', error);
                        Swal.fire({
                          icon: 'error',
                          title: 'Unable to update Yelp',
                          text: 'The Yelp status could not be saved.'
                        });
                      });
                    }
                    setYelpPromptClientId(null);
                  }}>
                  Yes
                </button>
                <button
                  className="h-12 rounded-xl border border-muted bg-secondary px-8 font-semibold"
                  onClick={() => setYelpPromptClientId(null)}>
                  No
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Facebook Connection Modal */}
          {facebookModalClient && (
            <NewFacebookPageConnection
              open={facebookModalOpen}
              onOpenChange={setFacebookModalOpen}
              currentClientId={facebookModalClient.id}
              currentClient={facebookModalClient}
              connectSnCallback={myCallback}
            />
          )}

          {/* Google Connection Modal */}
          {googleModalClient && (
            <NewGoogleConnection
              open={googleModalOpen}
              onOpenChange={setGoogleModalOpen}
              currentClientId={googleModalClient.id}
              currentClient={googleModalClient}
              connectSnCallback={myCallback}
            />
          )}

          {/* Instagram Connection Modal */}
          {instagramModalClient && (
            <NewInstagramConnection
              open={instagramModalOpen}
              onOpenChange={setInstagramModalOpen}
              currentClientId={instagramModalClient.id}
              currentClient={instagramModalClient}
              connectSnCallback={myCallback}
            />
          )}

          {/* Zoho Connection Modal */}
          {zohoModalClient && (
            <NewZohoConnection
              open={zohoModalOpen}
              onOpenChange={setZohoModalOpen}
              currentClientId={zohoModalClient.id}
              currentClient={zohoModalClient}
              connectSnCallback={myCallback}
            />
          )}

          {/* Facebook Sync Modal */}
          <Dialog
            open={fbSyncOpen}
            onOpenChange={(open) => {
              if (!open) setFbSyncOpen(false);
            }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-foreground">
                  Connect A Facebook Account
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Enter the Facebook Page ID to sync.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-2">
                <label className="text-xs text-muted-foreground font-medium">
                  Facebook Page ID
                </label>
                <Input
                  value={fbSyncPageId}
                  onChange={(e) => setFbSyncPageId(e.target.value)}
                  placeholder="e.g. 123456789"
                  className="rounded-xl"
                  disabled={fbSyncLoading}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="bg-secondary px-6 font-semibold rounded-xl h-10 border border-muted text-sm disabled:opacity-50"
                  onClick={() => setFbSyncOpen(false)}
                  disabled={fbSyncLoading}>
                  Cancel
                </button>
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 font-semibold rounded-xl h-10 text-sm flex items-center gap-2 disabled:opacity-70"
                  disabled={fbSyncLoading || !fbSyncPageId.trim()}
                  onClick={async () => {
                    setFbSyncLoading(true);
                    try {
                      await fetch(API_FACEBOOK_PAGES, {
                        method: 'POST',
                        body: JSON.stringify({ pageId: fbSyncPageId }),
                        headers: {
                          Authorization: `bearer ${userInfo.accessToken}`
                        }
                      });
                      setFbSyncOpen(false);
                      setSyncSuccess({
                        open: true,
                        title: 'Facebook sync complete!'
                      });
                    } catch {
                      Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: 'We cannot sync with Facebook'
                      });
                    } finally {
                      setFbSyncLoading(false);
                    }
                  }}>
                  {fbSyncLoading && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {fbSyncLoading ? 'Syncing...' : 'Sync'}
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Instagram Sync Modal */}
          <Dialog
            open={igSyncOpen}
            onOpenChange={(open) => {
              if (!open) setIgSyncOpen(false);
            }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-foreground">
                  Connect An Instagram Account
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Enter the Facebook Page ID linked to the Instagram account.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-2">
                <label className="text-xs text-muted-foreground font-medium">
                  Facebook Page ID
                </label>
                <Input
                  value={igSyncPageId}
                  onChange={(e) => setIgSyncPageId(e.target.value)}
                  placeholder="e.g. 123456789"
                  className="rounded-xl"
                  disabled={igSyncLoading}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="bg-secondary px-6 font-semibold rounded-xl h-10 border border-muted text-sm disabled:opacity-50"
                  onClick={() => setIgSyncOpen(false)}
                  disabled={igSyncLoading}>
                  Cancel
                </button>
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 font-semibold rounded-xl h-10 text-sm flex items-center gap-2 disabled:opacity-70"
                  disabled={igSyncLoading || !igSyncPageId.trim()}
                  onClick={async () => {
                    setIgSyncLoading(true);
                    try {
                      await fetch(INSTAGRAM_SYNC, {
                        method: 'POST',
                        body: JSON.stringify({ pageId: igSyncPageId })
                      });
                      setIgSyncOpen(false);
                      setSyncSuccess({
                        open: true,
                        title: 'Instagram sync complete!'
                      });
                    } catch {
                      Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: 'We cannot sync with Instagram'
                      });
                    } finally {
                      setIgSyncLoading(false);
                    }
                  }}>
                  {igSyncLoading && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {igSyncLoading ? 'Syncing...' : 'Sync'}
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Support Modal */}
          <Dialog
            open={supportOpen}
            onOpenChange={(open) => {
              if (!open) setSupportOpen(false);
            }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-foreground">
                  Update FB &amp; IG Tokens on Suite
                </DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">
                    Daniel's new FB token
                  </label>
                  <Input
                    value={danielToken}
                    onChange={(e) => setDanielToken(e.target.value)}
                    placeholder="Token"
                    className="rounded-xl"
                    disabled={supportLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">
                    Robin's new FB token
                  </label>
                  <Input
                    value={robinToken}
                    onChange={(e) => setRobinToken(e.target.value)}
                    placeholder="Token"
                    className="rounded-xl"
                    disabled={supportLoading}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="bg-secondary px-6 font-semibold rounded-xl h-10 border border-muted text-sm disabled:opacity-50"
                  onClick={() => setSupportOpen(false)}
                  disabled={supportLoading}>
                  Cancel
                </button>
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 font-semibold rounded-xl h-10 text-sm flex items-center gap-2 disabled:opacity-70"
                  disabled={supportLoading}
                  onClick={async () => {
                    setSupportLoading(true);
                    try {
                      await fetch(API_SUPPORT, {
                        method: 'POST',
                        body: JSON.stringify({
                          tokens: { danielToken, robinToken }
                        })
                      });
                      setSupportOpen(false);
                      setSyncSuccess({
                        open: true,
                        title: 'Tokens updated successfully!'
                      });
                    } catch {
                      Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: 'We cannot update'
                      });
                    } finally {
                      setSupportLoading(false);
                    }
                  }}>
                  {supportLoading && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {supportLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Sync Success Dialog */}
          <Dialog
            open={syncSuccess.open}
            onOpenChange={(open) => {
              if (!open) setSyncSuccess({ open: false, title: '' });
            }}>
            <DialogContent className="sm:max-w-xs text-center">
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="h-16 w-16 rounded-full bg-[hsl(142,60%,95%)] flex items-center justify-center">
                  <CheckCircle2 className="h-9 w-9 text-[hsl(142,60%,35%)]" />
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground">
                    {syncSuccess.title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    The operation completed successfully.
                  </p>
                </div>
                <button
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 font-semibold rounded-xl h-10 text-sm mt-2"
                  onClick={() => setSyncSuccess({ open: false, title: '' })}>
                  Done
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  );
};

function mapStateToProps(state) {
  let { errorLogin, loading, userInfo } = state.auth;
  let { loadingClients, errorClients, customerList } = state.clients;
  return {
    customerName: userInfo?.displayName,
    customerEmail: userInfo?.email,
    customerPhoto: userInfo?.photoURL,
    role: userInfo?.role,
    isLoggingOut: loading,
    logoutError: errorLogin,
    loadingClients,
    errorClients,
    customerList
  };
}
export default connect(mapStateToProps)(Dashboard);
